import { collection, doc, getDoc, getDocs, onSnapshot, query } from "firebase/firestore"
import { firestore } from "../firebase.ts"
import {
  StripeCustomerRecord,
  StripeInvoiceRecord,
  StripeSubscriptionRecord,
  StripeSubscriptionStatus,
  UserID,
} from "@cruisen/contract"
import Config from "../../config"

export const isSubscriptionEnabled = () => {
  return Date.now() >= Date.parse(Config.stripeSubscriptionLaunchDate)
}

export const terminatedSubscriptionStatuses: StripeSubscriptionStatus[] = ["paused", "canceled"]

export const activeSubscriptionStatuses: StripeSubscriptionStatus[] = [
  "active",
  "trialing",
  "past_due",
]

export const fetchStripeCustomerByUserId = async (
  userId: UserID,
): Promise<StripeCustomerRecord | null> => {
  try {
    console.log("fetch stripe customer", { userId })
    const customerDocRef = doc(firestore, "StripeCustomers", userId)
    const customerDoc = await getDoc(customerDocRef)

    if (!customerDoc.exists()) {
      console.log(`stripe customer with userId ${userId} not found`)
      return null
    }

    return customerDoc.data() as StripeCustomerRecord
  } catch (error) {
    console.error("error fetching stripe customer:", error)
    return null
  }
}

const findCurrentSubscription = (
  subscriptionDocs: StripeSubscriptionRecord[],
): StripeSubscriptionRecord => {
  subscriptionDocs.sort((a, b) => b.created.seconds - a.created.seconds)

  const activeSubscription = subscriptionDocs.find(
    (subscriptionDoc) => subscriptionDoc.status == "active",
  )
  if (activeSubscription) {
    return activeSubscription
  }

  const trialSubscription = subscriptionDocs.find(
    (subscriptionDoc) => subscriptionDoc.status == "trialing",
  )
  if (trialSubscription) {
    return trialSubscription
  }

  return subscriptionDocs.filter((subscriptionDoc) => {
    return (
      subscriptionDoc.status !== "incomplete" && subscriptionDoc.status !== "incomplete_expired"
    )
  })[0]
}

/**
 * @param {UserID} userId
 * @param {string} subscriptionId
 */
export const findLatestUnpaidInvoice = async (
  userId: UserID,
  subscriptionId: string,
): Promise<StripeInvoiceRecord | null> => {
  const invoicesCollectionSnapshot = await getDocs(
    collection(firestore, "StripeCustomers", userId, "subscriptions", subscriptionId, "invoices"),
  )

  const invoices = invoicesCollectionSnapshot.docs.map((invoice) => ({
    ...invoice.data(),
    id: invoice.id,
  })) as StripeInvoiceRecord[]

  invoices.sort((a, b) => b.created - a.created)

  return invoices.find((invoice) => invoice.status === "open" && !invoice.paid && !invoice.deleted)
}

export const fetchStripeSubscriptionByUserId = async (
  userId: UserID,
): Promise<StripeSubscriptionRecord | null> => {
  try {
    console.log("fetch stripe subscription", { userId })
    const subscriptionsSubCollectionRef = collection(
      doc(firestore, "StripeCustomers", userId),
      "subscriptions",
    )
    const subscriptionsSnapshot = await getDocs(subscriptionsSubCollectionRef)

    const subscriptionDocs = subscriptionsSnapshot.docs.map(
      (subscriptionSnapshot) =>
        ({
          ...subscriptionSnapshot.data(),
          id: subscriptionSnapshot.id,
        }) as StripeSubscriptionRecord,
    )

    return findCurrentSubscription(subscriptionDocs)
  } catch (error) {
    console.error("error fetching stripe customer:", error)
    return null
  }
}

export const onStripeSubscription = (
  userId: UserID,
  callback: (subscription: StripeSubscriptionRecord | null) => void,
) => {
  const subscriptionsQuery = query(
    collection(doc(firestore, "StripeCustomers", userId), "subscriptions"),
  )

  return onSnapshot(subscriptionsQuery, (subscriptionsQuerySnapshot) => {
    const subscriptionDocs: StripeSubscriptionRecord[] = []
    subscriptionsQuerySnapshot.forEach((subscriptionSnapshot) => {
      subscriptionDocs.push({
        ...subscriptionSnapshot.data(),
        id: subscriptionSnapshot.id,
      } as StripeSubscriptionRecord)
    })

    callback(findCurrentSubscription(subscriptionDocs))
  })
}
